import { useEffect, useState } from 'react';
import { onStageMessage, postStage } from './lessonStage';

/** A clock not heard from in this long is a video that stopped or closed. */
export const VIDEO_CLOCK_STALE_MS = 3000;

/**
 * Where the stage's video is (pass 4 S4, 2026-10-02): the stage reports
 * `video-clock` { videoId, t, playing } as YouTube tells it; between reports
 * the time runs on from the moment it arrived, so the highlight moves
 * smoothly rather than in quarter-second steps. Null when no video has
 * reported recently. `seek(videoId, t)` asks the stage to jump there.
 */
export const videoClockNow = (clock, nowMs) => {
	// YouTube reports while PLAYING; a paused video goes quiet but is still
	// there, so only a playing clock goes stale (the video closed or stalled).
	if (!clock || (clock.playing && nowMs - clock.at > VIDEO_CLOCK_STALE_MS)) return null;
	return { videoId: clock.videoId, playing: clock.playing, t: clock.t + (clock.playing ? (nowMs - clock.at) / 1000 : 0) };
};

const useStageVideoClock = () => {
	const [clock, setClock] = useState(null);
	const [now, setNow] = useState(() => Date.now());

	useEffect(
		() =>
			onStageMessage((m) => {
				if (m.type !== 'video-clock' || !m.videoId || typeof m.t !== 'number') return;
				setClock({ videoId: m.videoId, t: m.t, playing: Boolean(m.playing), at: Date.now() });
			}),
		[]
	);

	// Tick only while a video PLAYS: the page re-renders on each tick, so a
	// paused or absent video costs nothing.
	useEffect(() => {
		if (!clock || !clock.playing) return undefined;
		const id = setInterval(() => setNow(Date.now()), 250);
		return () => clearInterval(id);
	}, [clock]);

	return {
		current: videoClockNow(clock, Math.max(now, clock ? clock.at : 0)),
		seek: (videoId, t) => postStage({ type: 'video-seek', videoId, t }),
	};
};

export default useStageVideoClock;
